import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit } from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";

const ScheduleNotesNew = () => {
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [note, setNote] = useState(null);
  const [search, setSearch] = useState(null);
  const [notesData, setNotesData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [schedules, setSchedules] = useState([]);
  const [noteTypes, setNoteTypes] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ScheduleNoteTypeID: "",
      ScheduleID: "",
      Memo: "",
      Private: false,
      Active: true,
    },
  });

  useEffect(() => {
    if (note) {
      reset({
        ScheduleNoteTypeID: note.ScheduleNoteTypeID,
        ScheduleID: note.ScheduleID,
        Memo: note.Memo,
        Private: note.Private === true || note.Private === 1,
        Active: note.Active === true || note.Active === 1,
      });
    }
  }, [note, reset]);

  useEffect(() => {
    const fetchLookups = async () => {
      const { data: Schedules } = await supabase
        .from("Schedule")
        .select("ScheduleID, FlightNo, ScheduleDate");
      setSchedules(Schedules || []);
      const { data: NoteTypes } = await supabase
        .from("ScheduleNoteTypes")
        .select("ScheduleNoteTypeID, Description");
      setNoteTypes(NoteTypes || []);
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    const fetchNotes = async () => {
      let query = supabase
        .from("ScheduleNotes")
        .select(
          "ScheduleNoteID, ScheduleNoteTypeID, ScheduleID, Memo, Private, Active, CreatedDate",
          { count: "exact" }
        )
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
      if (search && search.text) {
        query = query.ilike(search.type, `%${search.text}%`);
      }
      const { data, count, error } = await query;
      if (!error) {
        setNotesData(data);
        setTotalCount(count);
      }
    };
    fetchNotes();
  }, [page, search, isEdit]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  const onPrevPage = useCallback(() => {
    setPage((old) => Math.max(old - 1, 1));
  }, []);

  const onSuccess = useCallback(() => {
    toast.success("Success");
    setIsEdit(false);
    setNote(null);
  }, []);
  const onError = useCallback((err) => {
    toast.error("Error: " + err.message);
  }, []);

  const onAddNew = () => {
    reset({
      ScheduleNoteTypeID: noteTypes[0] ? noteTypes[0].ScheduleNoteTypeID : "",
      ScheduleID: schedules[0] ? schedules[0].ScheduleID : "",
      Memo: "",
      Private: false,
      Active: true,
    });
    setIsEdit(true);
    setNote(null);
  };

  const OnCancel = () => {
    setIsEdit(false);
    setNote(null);
  };

  const OnCreate = useCallback(
    async (data) => {
      if (note) {
        // Update
        const { error } = await supabase
          .from("ScheduleNotes")
          .update({
            ScheduleNoteTypeID: data.ScheduleNoteTypeID,
            ScheduleID: data.ScheduleID,
            Memo: data.Memo,
            Private: data.Private ? 1 : 0,
            Active: data.Active ? 1 : 0,
          })
          .eq("ScheduleNoteID", note.ScheduleNoteID);
        if (error) onError(error);
        else onSuccess();
      } else {
        // Create
        const { error } = await supabase.from("ScheduleNotes").insert({
          ScheduleNoteTypeID: data.ScheduleNoteTypeID,
          ScheduleID: data.ScheduleID,
          Memo: data.Memo,
          Private: data.Private ? 1 : 0,
          Active: data.Active ? 1 : 0,
        });
        if (error) onError(error);
        else onSuccess();
      }
    },
    [note, onError, onSuccess]
  );

  const OnSearch = useCallback((data) => {
    setSearch(data);
  }, []);

  return (
    <>
      {isEdit ? (
        <div className="driver-patent-edit-content mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>Schedule</div>
            <div className="w-35">
              <Controller
                control={control}
                name="ScheduleID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.ScheduleID?.message}
                  >
                    {schedules.map((s) => (
                      <option key={s.ScheduleID} value={s.ScheduleID}>
                        {s.FlightNo} (
                        {s.ScheduleDate ? s.ScheduleDate.split("T")[0] : ""})
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Note Type</div>
            <div className="w-35">
              <Controller
                control={control}
                name="ScheduleNoteTypeID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.ScheduleNoteTypeID?.message}
                  >
                    {noteTypes.map((type) => (
                      <option
                        key={type.ScheduleNoteTypeID}
                        value={type.ScheduleNoteTypeID}
                      >
                        {type.Description}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Memo</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Memo"
                rules={{ required: "Memo is required" }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.Memo?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Private</div>
            <div className="w-35">
              <Controller
                control={control}
                name="Private"
                render={({ field: { value, onChange } }) => (
                  <input
                    type="checkbox"
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Active</div>
            <div className="w-35">
              <Controller
                control={control}
                name="Active"
                render={({ field: { value, onChange } }) => (
                  <input
                    type="checkbox"
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex gap-4 mt-4 save-button">
            <button
              disabled={false}
              onClick={handleSubmit(OnCreate)}
              className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              <span>{note ? "Update" : "Create"}</span>
            </button>
            <div>
              <div
                onClick={OnCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex ">
            <h5 className="mb-0">Schedule Notes</h5>
            <button
              className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
              onClick={onAddNew}
            >
              <span>Add New</span>
            </button>
          </div>
          <SearchBar setSearch={setSearch} OnSearch={OnSearch} />
          <table className="table-auto w-full mt-4 border border-gray-200">
            <thead>
              <tr className="bg-gray-100 cursor-pointer">
                <th className="px-4 py-2 border">ID</th>
                <th className="px-4 py-2 border">Schedule</th>
                <th className="px-4 py-2 border">Note Type</th>
                <th className="px-4 py-2 border">Memo</th>
                <th className="px-4 py-2 border">Private</th>
                <th className="px-4 py-2 border">Active</th>
                <th className="px-4 py-2 border">Created Date</th>
                <th className="px-4 py-2 border">Edit</th>
              </tr>
            </thead>
            <tbody>
              {notesData &&
                notesData.map((type) => (
                  <tr key={type.ScheduleNoteID} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border">{type.ScheduleNoteID}</td>
                    <td className="px-4 py-2 border">
                      {schedules.find((s) => s.ScheduleID === type.ScheduleID)
                        ? `${
                            schedules.find(
                              (s) => s.ScheduleID === type.ScheduleID
                            ).FlightNo
                          } (${
                            schedules
                              .find((s) => s.ScheduleID === type.ScheduleID)
                              .ScheduleDate?.split("T")[0]
                          })`
                        : ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {noteTypes.find(
                        (n) => n.ScheduleNoteTypeID === type.ScheduleNoteTypeID
                      )?.Description || ""}
                    </td>
                    <td className="px-4 py-2 border">{type.Memo}</td>
                    <td className="px-4 py-2 border">
                      {type.Private ? "Yes" : "No"}
                    </td>
                    <td className="px-4 py-2 border">
                      {type.Active ? "Yes" : "No"}
                    </td>
                    <td className="px-4 py-2 border">
                      {type.CreatedDate ? type.CreatedDate.split("T")[0] : ""}
                    </td>
                    <td className="px-4 py-2 border cursor-point">
                      <FaEdit
                        onClick={() => {
                          setNote(type);
                          setIsEdit(true);
                        }}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * PAGE_SIZE + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * PAGE_SIZE)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page == 1}
                    className={`pagination-item ${page == 1 ? "disabled" : ""}`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * PAGE_SIZE > totalCount}
                    className={`pagination-item ${
                      page * PAGE_SIZE > totalCount ? "disabled" : ""
                    }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScheduleNotesNew;

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "Memo",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center">
      <div>
        <p className="mb-0">Look For:</p>
      </div>
      <div className="flex justify-between w-10 ml-1">
        <Controller
          control={searchControl}
          name="type"
          render={({ field: { value, onChange } }) => (
            <select
              className="custom-input rounded-lg cursor-pointer"
              value={value}
              onChange={onChange}
            >
              <option value={"Memo"}>Memo</option>
              <option value={"Private"}>Private</option>
            </select>
          )}
        />
      </div>
      <div className="ml-1">
        <Controller
          control={searchControl}
          name="text"
          rules={{ required: "Required" }}
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              value={value}
              onChange={onChange}
              className="custom-input rounded-lg w-100 "
            />
          )}
        />
      </div>
      <div className="ml-1 flex">
        <div>
          <button
            onClick={handleSearchSubmit(OnSearch)}
            className="justify-between rounded-lg flex items-center w-30 custom-search-button border-none"
          >
            <span>Search</span>
          </button>
        </div>
        <div>
          <button
            onClick={() => {
              searchReset({ type: "Memo", text: "" });
              setSearch(null);
            }}
            className="justify-between rounded-lg ml-1 flex items-center custom-reset-button"
          >
            <span>Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
};
